import { createTheme } from '@mui/material/styles';

/**
 * Pro Portal design system — "Piper" redesign.
 *
 * Token-driven MUI theme implementing the Piper prototype visual language:
 * dense Inter type at small sizes, flat surfaces with hairline borders,
 * brand-red primary actions, tinted status surfaces, and a slate ink scale.
 *
 * All Piper components consume the exported `piper` token object (also
 * attached to the theme as `theme.piper`) — no hardcoded one-off colors.
 */

// ── Piper design tokens ──
export const piper = {
  // Brand red (primary action)
  red: '#e10404',
  redDark: '#b00303',
  // Red tints (attention surfaces)
  redTintBg: '#fef7f7',
  redTintBg2: '#fef2f2',
  redTintBorder: '#f4c2c2',
  // Ink scale
  ink: '#1f292e',
  inkSecondary: '#37474f',
  slate: '#546e7a',
  navRail: '#263238',
  // Muted text via alpha
  muted: 'rgba(0,0,0,.6)',
  muted50: 'rgba(0,0,0,.5)',
  muted45: 'rgba(0,0,0,.45)',
  label: 'rgba(0,0,0,.4)',
  // Green — Piper activity / success / verified
  green: '#1b5e20',
  green2: '#2e7d32',
  greenBg: '#e4f3e5',
  greenBg2: '#f2faf3',
  greenBorder: '#cde8d0',
  // Amber — caution
  amber: '#7b5800',
  amberBg: '#fff4d6',
  // Blue — informational
  blue: '#0d47a1',
  blueBg: '#e3effd',
  // Surfaces
  appBg: '#f5f6f8',
  surface: '#ffffff',
  subtleFill: '#eef1f3',
  subtleFill2: '#f8f9fa',
  // Borders — hairlines only
  hairline: 'rgba(0,0,0,.07)',
  hairlineLight: 'rgba(0,0,0,.06)',
  hairlineStrong: 'rgba(0,0,0,.09)',
  outline: 'rgba(0,0,0,.15)',
  // Shape
  radius: {
    control: 6,   // buttons + inputs
    card: 10,     // cards
    panel: 12,    // panels
    pill: 20,     // chips / pills
    bubble: '12px 12px 12px 4px', // chat bubbles (flat corner at speaker)
  },
  // Elevation — flat design; shadows for overlays + hero card only
  shadow: {
    overlay: '0 8px 24px rgba(0,0,0,.25)',
    heroGlow: '0 2px 10px rgba(225,4,4,.08)',
  },
  // Recurrent text treatments
  font: "'Inter', system-ui, sans-serif",
  microLabel: {
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: 'rgba(0,0,0,.45)',
  },
};

// ── Core brand ramps (legacy consumers) ──
export const slate = {
  50: '#f6f8f9',
  100: '#eceff2',
  200: '#d9e0e4',
  300: '#b9c6cd',
  400: '#8fa3ad',
  500: '#64808c',
  600: '#4b626e',
  700: '#3c505b',
  800: '#2c3d46',
  900: '#1d2b33',
  950: '#131e24',
};

export const red = {
  main: piper.red,
  light: '#ff4444',
  dark: piper.redDark,
};

// ── Status badge tokens (tinted bg + strong text), per scheme ──
const lightBadges = {
  success:   { bg: piper.greenBg, color: piper.green },
  danger:    { bg: piper.redTintBg2, color: piper.redDark },
  warning:   { bg: piper.amberBg, color: piper.amber },
  info:      { bg: piper.blueBg, color: piper.blue },
  primary:   { bg: piper.subtleFill, color: piper.inkSecondary },
  secondary: { bg: piper.subtleFill2, color: piper.slate },
  purple:    { bg: '#ede9fe', color: '#6d28d9' },
  cyan:      { bg: '#cffafe', color: '#0e7490' },
  teal:      { bg: '#ccfbf1', color: '#0f766e' },
  azure:     { bg: '#e0f2fe', color: '#0369a1' },
};

const darkBadges = {
  success:   { bg: 'rgba(74, 222, 128, 0.14)', color: '#6ee7a0' },
  danger:    { bg: 'rgba(248, 113, 113, 0.14)', color: '#fca5a5' },
  warning:   { bg: 'rgba(250, 204, 21, 0.13)', color: '#fbd66f' },
  info:      { bg: 'rgba(96, 165, 250, 0.14)', color: '#93c5fd' },
  primary:   { bg: 'rgba(185, 198, 205, 0.14)', color: slate[200] },
  secondary: { bg: 'rgba(148, 163, 173, 0.13)', color: '#aeb9c0' },
  purple:    { bg: 'rgba(167, 139, 250, 0.14)', color: '#c4b5fd' },
  cyan:      { bg: 'rgba(34, 211, 238, 0.13)', color: '#67e8f9' },
  teal:      { bg: 'rgba(45, 212, 191, 0.13)', color: '#5eead4' },
  azure:     { bg: 'rgba(56, 189, 248, 0.13)', color: '#7dd3fc' },
};

/** Normalize #RGB / #RRGGBB (optional leading #) to #RRGGBB. */
function normalizeHex(color) {
  if (!color || typeof color !== 'string') return null;
  let hex = color.trim();
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  return hex.toUpperCase();
}

/** Derive a darker shade of a hex color (factor < 1). */
function darkenHex(hex, factor = 0.78) {
  const normalized = normalizeHex(hex);
  if (!normalized) return piper.redDark;
  const r = Math.round(parseInt(normalized.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(normalized.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(normalized.slice(5, 7), 16) * factor);
  const to = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function lightenHex(hex, mix = 0.35) {
  const normalized = normalizeHex(hex);
  if (!normalized) return '#ff4444';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const blend = (c) => Math.round(c + (255 - c) * mix);
  const to = (n) => n.toString(16).padStart(2, '0');
  return `#${to(blend(r))}${to(blend(g))}${to(blend(b))}`;
}

/**
 * @param {{ primaryColor?: string, secondaryColor?: string } | null} [branding]
 */
export const createAppTheme = (branding) => {
  const primaryMain = normalizeHex(branding?.primaryColor) || piper.red;
  const primaryDark = branding?.primaryColor ? darkenHex(primaryMain) : piper.redDark;
  const primaryLight = lightenHex(primaryMain);
  const secondaryMain = normalizeHex(branding?.secondaryColor) || piper.slate;

  const tokens = {
    ...piper,
    red: primaryMain,
    redDark: primaryDark,
    shadow: {
      ...piper.shadow,
      heroGlow: `0 2px 10px ${primaryMain}14`,
    },
  };

  const brandedLightBadges = {
    ...lightBadges,
    danger: { bg: piper.redTintBg2, color: primaryDark },
  };

  const theme = createTheme({
    cssVariables: {
      colorSchemeSelector: 'data-color-scheme',
    },

    colorSchemes: {
      light: {
        palette: {
          // Brand primary from tenant branding (defaults to Piper red).
          primary: {
            main: primaryMain,
            light: primaryLight,
            dark: primaryDark,
            contrastText: '#ffffff',
          },
          // Slate (or tenant secondary) becomes the secondary / structural color.
          secondary: {
            main: secondaryMain,
            light: slate[400],
            dark: slate[800],
            contrastText: '#ffffff',
          },
          error: { main: primaryDark },
          warning: { main: piper.amber },
          info: { main: piper.blue },
          success: { main: piper.green2 },
          background: {
            default: piper.appBg,
            paper: piper.surface,
          },
          text: {
            primary: piper.ink,
            secondary: piper.slate,
          },
          divider: piper.hairlineStrong,
          badges: brandedLightBadges,
          highlight: {
            field: piper.amberBg,
          },
        },
      },
      // Dark scheme retained for plumbing; visual tuning deferred (redesign is light-first).
      dark: {
        palette: {
          primary: {
            main: primaryLight,
            light: lightenHex(primaryMain, 0.55),
            dark: primaryMain,
            contrastText: '#ffffff',
          },
          secondary: { main: '#9fb7c2', light: '#c3d3db', dark: '#7695a3', contrastText: slate[950] },
          error: { main: '#f97066' },
          warning: { main: '#fdb022' },
          info: { main: '#53b1fd' },
          success: { main: '#47cd89' },
          background: {
            default: '#10181c',
            paper: '#161f25',
          },
          text: {
            primary: '#e8edf0',
            secondary: 'rgba(232, 237, 240, 0.64)',
          },
          divider: 'rgba(232, 237, 240, 0.10)',
          badges: darkBadges,
          highlight: {
            field: 'rgba(250, 204, 21, 0.12)',
          },
        },
      },
    },

    typography: {
      // Inter only — matches the Piper prototype (no Roboto/Arial fallbacks that change the look).
      fontFamily: "'Inter', system-ui, sans-serif",
      // Deliberately dense and small per the Piper prototype.
      h1: { fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.015em' },
      h3: { fontWeight: 700, fontSize: '1.075rem', letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.005em' },
      h5: { fontWeight: 700, fontSize: '0.875rem' },
      h6: { fontWeight: 600, fontSize: '0.8125rem' },
      subtitle1: { fontWeight: 600, fontSize: '0.8125rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.78125rem' },
      body1: { fontSize: '0.8125rem', lineHeight: 1.55 },   // ~13px
      body2: { fontSize: '0.75rem', lineHeight: 1.55 },     // 12px
      caption: { fontSize: '0.71875rem', lineHeight: 1.45 }, // ~11.5px
      overline: {
        fontSize: '0.65625rem', // ~10.5px micro-label
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        lineHeight: 1.6,
      },
      button: { fontSize: '0.75rem', fontWeight: 600, fontFamily: "'Inter', system-ui, sans-serif" },
    },

    shape: {
      borderRadius: 6,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: `
          html, body, button, input, textarea, select {
            font-family: 'Inter', system-ui, sans-serif;
          }
          body {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
          }
          #root {
            min-height: 100vh;
          }
          .tabular-nums {
            font-variant-numeric: tabular-nums;
          }
          .cursor-pointer {
            cursor: pointer;
            user-select: none;
          }
          @keyframes pageFadeIn {
            from { opacity: 0; transform: translateY(4px); }
            to { opacity: 1; transform: none; }
          }
          .page-fade-in {
            animation: pageFadeIn 0.25s ease-out;
          }
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
              animation-duration: 0.001ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.001ms !important;
              scroll-behavior: auto !important;
            }
          }
        `,
      },

      // ── Buttons — flat, 6px radius, dense ──
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme: t }) => ({
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: piper.radius.control,
            padding: '7px 14px',
            boxShadow: 'none',
            transition: 'background-color .15s ease, border-color .15s ease, transform .1s ease',
            '&:active': { transform: 'scale(0.985)' },
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px rgba(${t.vars.palette.primary.mainChannel} / 0.3)`,
            },
          }),
          sizeSmall: { fontSize: '0.71875rem', padding: '4px 10px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: ({ theme: t }) => ({
            borderColor: piper.outline,
            color: piper.inkSecondary,
            backgroundColor: piper.surface,
            '&:hover': {
              borderColor: `rgba(${t.vars.palette.text.primaryChannel} / 0.3)`,
              backgroundColor: piper.subtleFill2,
            },
            ...t.applyStyles('dark', {
              backgroundColor: 'transparent',
              color: t.vars.palette.text.primary,
            }),
          }),
          text: { fontWeight: 700 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px rgba(${t.vars.palette.primary.mainChannel} / 0.3)`,
            },
          }),
        },
      },

      // ── Inputs — 6px radius, hairline outline ──
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            fontSize: '0.78125rem',
            borderRadius: piper.radius.control,
            backgroundColor: t.vars.palette.background.paper,
            transition: 'box-shadow 0.15s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: piper.outline,
              transition: 'border-color 0.15s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `rgba(${t.vars.palette.text.primaryChannel} / 0.3)`,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px rgba(${t.vars.palette.primary.mainChannel} / 0.12)`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: t.vars.palette.primary.main,
                borderWidth: 1.5,
              },
            },
          }),
          input: { padding: '8px 11px' },
          inputSizeSmall: { padding: '6px 10px' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.78125rem' },
          sizeSmall: { fontSize: '0.71875rem' },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontSize: '0.6875rem', marginTop: 2 } },
      },
      MuiSelect: {
        styleOverrides: {
          select: { fontSize: '0.78125rem' },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.78125rem', minHeight: 32, borderRadius: 6, margin: '0 4px', paddingLeft: 8, paddingRight: 8 },
        },
      },

      // ── Cards — flat white with hairline border ──
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: piper.hairlineStrong,
            borderRadius: piper.radius.panel,
            boxShadow: 'none',
            backgroundImage: 'none',
            ...t.applyStyles('dark', {
              borderColor: t.vars.palette.divider,
            }),
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: '14px 16px', '&:last-child': { paddingBottom: 14 } },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '12px 16px' },
          title: { fontSize: '0.84375rem', fontWeight: 600 },
          subheader: { fontSize: '0.71875rem' },
        },
      },

      // ── Tables — dense, hairline dividers ──
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            fontSize: '0.78125rem',
            padding: '8px 12px',
            borderColor: piper.hairline,
            ...t.applyStyles('dark', { borderColor: t.vars.palette.divider }),
          }),
          head: ({ theme: t }) => ({
            fontWeight: 700,
            fontSize: '0.65625rem',
            color: piper.label,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            padding: '6px 12px',
            backgroundColor: piper.subtleFill2,
            whiteSpace: 'nowrap',
            ...t.applyStyles('dark', {
              color: t.vars.palette.text.secondary,
              backgroundColor: t.vars.palette.background.default,
            }),
          }),
          sizeSmall: { padding: '5px 10px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            '&.MuiTableRow-hover:hover': {
              backgroundColor: piper.subtleFill2,
              ...t.applyStyles('dark', {
                backgroundColor: `rgba(${t.vars.palette.text.primaryChannel} / 0.04)`,
              }),
            },
          }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: { fontSize: '0.65625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' },
        },
      },

      // ── Chips — full-round tinted pills ──
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: piper.radius.pill, height: 22, fontWeight: 600 },
          sizeSmall: { fontSize: '0.671875rem', height: 20 },
          label: { paddingLeft: 9, paddingRight: 9 },
          labelSmall: { paddingLeft: 8, paddingRight: 8 },
        },
      },

      // ── Tabs — red active underline ──
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
          indicator: { height: 2.5, borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.78125rem',
            minHeight: 36,
            padding: '8px 14px',
            color: 'rgba(0,0,0,.55)',
            transition: 'color .15s ease',
            '&:hover': { color: t.vars.palette.text.primary },
            '&.Mui-selected': { fontWeight: 700 },
            ...t.applyStyles('dark', { color: t.vars.palette.text.secondary }),
          }),
        },
      },

      // ── Dialogs — the one place shadows live ──
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            borderRadius: piper.radius.panel,
            boxShadow: piper.shadow.overlay,
            backgroundImage: 'none',
            ...t.applyStyles('dark', {
              border: `1px solid ${t.vars.palette.divider}`,
            }),
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '0.9375rem', fontWeight: 700, padding: '14px 20px' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '12px 20px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '10px 20px' },
        },
      },

      // ── AppBar ──
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            boxShadow: 'none',
            borderBottom: `1px solid ${t.vars.palette.divider}`,
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: '50px !important' },
        },
      },

      // ── Drawer ──
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            borderRight: `1px solid ${t.vars.palette.divider}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          }),
        },
      },

      // ── Lists ──
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: 6,
            padding: '5px 10px',
            transition: 'background-color .15s ease, color .15s ease',
            '&.Mui-selected': {
              backgroundColor: piper.subtleFill,
              '&:hover': { backgroundColor: piper.subtleFill },
              ...t.applyStyles('dark', {
                backgroundColor: `rgba(${t.vars.palette.text.primaryChannel} / 0.08)`,
              }),
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 32 },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: '0.78125rem' },
        },
      },

      // ── Menus / Selects — overlays get shadow ──
      MuiMenu: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: {
          paper: ({ theme: t }) => ({
            borderRadius: piper.radius.card,
            boxShadow: piper.shadow.overlay,
            border: `1px solid ${t.vars.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },

      // ── Alerts ──
      MuiAlert: {
        styleOverrides: {
          root: { fontSize: '0.78125rem', borderRadius: piper.radius.card, padding: '6px 14px' },
          standardSuccess: { backgroundColor: piper.greenBg2, color: piper.green, border: `1px solid ${piper.greenBorder}` },
          standardError: { backgroundColor: piper.redTintBg, color: tokens.redDark, border: `1px solid ${piper.redTintBorder}` },
          standardWarning: { backgroundColor: piper.amberBg, color: piper.amber },
          standardInfo: { backgroundColor: piper.blueBg, color: piper.blue },
        },
      },

      // ── Tooltips ──
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme: t }) => ({
            fontSize: '0.71875rem',
            borderRadius: piper.radius.control,
            backgroundColor: piper.ink,
            ...t.applyStyles('dark', {
              backgroundColor: slate[100],
              color: slate[900],
            }),
          }),
        },
      },

      // ── Paper — flat everywhere ──
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: ({ theme: t }) => ({ borderColor: t.vars.palette.divider }),
        },
      },

      // ── Dividers ──
      MuiDivider: {
        styleOverrides: {
          root: ({ theme: t }) => ({ borderColor: t.vars.palette.divider }),
        },
      },

      // ── Accordion — flat card sections ──
      MuiAccordion: {
        defaultProps: { disableGutters: true },
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: `${piper.radius.card}px !important`,
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: `1px solid ${t.vars.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { minHeight: 42, '&.Mui-expanded': { minHeight: 42 } },
          content: { margin: '8px 0', '&.Mui-expanded': { margin: '8px 0' } },
        },
      },

      // ── Skeleton ──
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },

      // ── Toggle buttons ──
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderColor: piper.outline,
            '&.Mui-selected': {
              backgroundColor: piper.slate,
              color: '#fff',
              '&:hover': { backgroundColor: slate[700] },
              ...t.applyStyles('dark', {
                backgroundColor: `rgba(${t.vars.palette.text.primaryChannel} / 0.14)`,
                color: t.vars.palette.text.primary,
              }),
            },
          }),
        },
      },

      // ── Switch (Full Piper mode toggle etc.) ──
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 6 },
          thumb: { boxShadow: 'none' },
        },
      },
    },
  });

  // Expose Piper tokens (with branding overrides) so `sx` callbacks + styled() can reach them.
  theme.piper = tokens;
  return theme;
};
